import * as React from 'react';

import { Col } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { useExternalRoleUpdateMutation } from './_graphql/useExternalRoleUpdateMutation';

import {
  FormFooter,
  PermissionsEditor,
} from '~/modules/settings/_components/_shared';
import { externalPermissionModules } from '~/modules/settings/_generated/permissions';
import {
  $bulkUpdateRolePermissions,
  $currentRole,
  $enabledPermissionKeys,
  $isRoleFormDirty,
  $originalRolePermissions,
  $resetRolePermissions,
  $rolePermissions,
  $updateRolePermission,
} from '~/modules/settings/_state/roleForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

export const CustomerPortalRolePermissionsTab = () => {
  const t = useTranslations();
  const permissions = useAtomValue($rolePermissions);
  const isDirty = useAtomValue($isRoleFormDirty);
  const currentRole = useAtomValue($currentRole);
  const enabledKeys = useAtomValue($enabledPermissionKeys);
  const updatePermission = useSetAtom($updateRolePermission);
  const bulkUpdatePermissions = useSetAtom($bulkUpdateRolePermissions);
  const resetPermissions = useSetAtom($resetRolePermissions);
  const setOriginalPermissions = useSetAtom($originalRolePermissions);

  const updateRoleMutation = useExternalRoleUpdateMutation();

  const handlePermissionChange = React.useCallback(
    (key: string, enabled: boolean) => {
      updatePermission({ key, enabled });
    },
    [updatePermission],
  );

  const handleBulkChange = React.useCallback(
    (changes: Record<string, boolean>) => {
      bulkUpdatePermissions(changes);
    },
    [bulkUpdatePermissions],
  );

  const handleDiscard = React.useCallback(() => {
    resetPermissions();
  }, [resetPermissions]);

  const handleSave = React.useCallback(async () => {
    if (!currentRole) return;

    try {
      await updateRoleMutation.mutateAsync({
        id: currentRole.id,
        permissions: enabledKeys,
      });
      setOriginalPermissions({ ...permissions });
    } catch (_error) {
      console.error('Failed to update role permissions:', _error);
    }
  }, [
    currentRole,
    enabledKeys,
    permissions,
    updateRoleMutation,
    setOriginalPermissions,
  ]);

  return (
    <Col className="@CustomerPortalRolePermissionsTab h-full">
      <div className="flex-1 overflow-y-auto p-5">
        <PermissionsEditor
          modules={externalPermissionModules}
          value={permissions}
          onChange={handlePermissionChange}
          onBulkChange={handleBulkChange}
        />
      </div>
      <FormFooter
        onDiscard={handleDiscard}
        onSave={handleSave}
        isDiscardDisabled={!isDirty}
        isSaveDisabled={!isDirty}
        isSaveLoading={updateRoleMutation.isPending}
        discardLabel={t.customerPortal.actions.discard}
        saveLabel={t.customerPortal.actions.save}
      />
    </Col>
  );
};
